import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {useFormContext, useWatch} from 'react-hook-form'
import {InputAddressAutocomplete} from '@/components/commons/input-address-autocomplete/InputAddressAutocomplete'
import {MarkerPin01Icon, MedicalCrossIcon} from '@/components/ui/icon/Icon'
import {UserAddressCard} from '@/features/users/components/user-address-card/UserAddressCard'
import {StyledDomicileAddressCardWrapper, StyledDomicileAddressHint} from './style'
import {Button} from '@/components/ui/button/Button'
import {useEffect, useMemo, useState} from 'react'
import {UserDomicileManualFormModal} from '@/features/users/components/user-domicile-manual-form-modal/UserDomicileManualFormModal'
import {Switch} from '@/components/commons/switch/Switch'
import {useGetAslByCoordinates} from '@/services/useGetAslByCoordinates'
import {Coordinates} from '@/types'
import {retrieveAddressDataFromComponents} from '@/features/users/utils'
import {UserDomicileFormValidationSchema} from '@/features/users/types'

export const UserDomicileForm = ({defaultCoordinates}: {defaultCoordinates?: Coordinates}) => {
    const {t} = useTranslation()
    const [addressCoordinates, setAddressCoordinates] = useState<Coordinates | null>(defaultCoordinates ?? null)
    const [isManualFormModalActive, setIsManualFormModalActive] = useState(false)

    // queries
    const aslDistrictQuery = useGetAslByCoordinates({
        coordinates: addressCoordinates ?? undefined,
        responseType: 'extended',
        options: {enabled: !!addressCoordinates && !isManualFormModalActive}
    })

    // methods
    const form = useFormContext<UserDomicileFormValidationSchema>()

    // actions
    const onChangeToggleSwitch = (value: boolean) => {
        form.setValue('domicile.address', '', {shouldDirty: true})
        form.setValue('domicile.streetNumber', '', {shouldDirty: true})
        form.setValue('domicile.city', null, {shouldDirty: true})
        form.setValue('domicile.province', null, {shouldDirty: true})
        form.setValue('domicile.postalCode', '', {shouldDirty: true})
        form.setValue('domicileAslDistrict', null, {shouldDirty: true})
        form.setValue('isDifferentDomicile', value)
        form.clearErrors('domicile')
    }

    const onSelectAddressAutocomplete = (fullAddressValue: google.maps.GeocoderResult | null) => {
        if (fullAddressValue) {
            const {address, city, streetNumber, province, zipCode} = retrieveAddressDataFromComponents(
                fullAddressValue.address_components
            )
            form.setValue('domicile.address', address, {shouldDirty: true, shouldValidate: true})
            form.setValue('domicile.streetNumber', streetNumber, {shouldDirty: true, shouldValidate: true})
            form.setValue('domicile.city', city, {shouldDirty: true, shouldValidate: true})
            form.setValue('domicile.province', province, {shouldDirty: true, shouldValidate: true})
            form.setValue('domicile.postalCode', zipCode, {shouldDirty: true, shouldValidate: true})
            // clear the previous ASL value before fetching the coordinate-based ASL
            form.setValue('domicileAslDistrict', null)
            form.clearErrors('domicile')

            if (!address || !city || !province || !zipCode) {
                setIsManualFormModalActive(true)
            }
        }
    }

    // form observer
    const isDifferentDomicile = useWatch({name: 'isDifferentDomicile', control: form.control})
    const domicileAddress = useWatch({control: form.control, name: 'domicile.address'})
    const domicileStreetNumber = useWatch({control: form.control, name: 'domicile.streetNumber'})
    const domicilePostalCode = useWatch({control: form.control, name: 'domicile.postalCode'})
    const domicileCity = useWatch({control: form.control, name: 'domicile.city'})
    const domicileProvince = useWatch({control: form.control, name: 'domicile.province'})
    const domicileAslDistrict = useWatch({control: form.control, name: 'domicileAslDistrict'})

    // set initial value
    useEffect(() => {
        if (aslDistrictQuery.data) {
            form.setValue('domicileAslDistrict', aslDistrictQuery.data)
        }
    }, [aslDistrictQuery.data])

    // trigger changes
    useEffect(() => {
        if (aslDistrictQuery.status == 'success' && domicileAslDistrict?.id != aslDistrictQuery.data.id) {
            form.setValue('domicileAslDistrict', aslDistrictQuery.data, {shouldDirty: true})
        }
        if (aslDistrictQuery.status == 'error' && domicileAslDistrict?.id) {
            form.setValue('domicileAslDistrict', null, {shouldDirty: true})
        }
    }, [aslDistrictQuery?.status])

    // booleans
    const isOutOfRegion = useMemo(
        () =>
            (aslDistrictQuery.isFetched && !domicileAslDistrict?.asl?.id) ||
            (aslDistrictQuery.isError && !domicileAslDistrict?.asl?.id),
        [aslDistrictQuery, domicileAslDistrict?.asl?.id]
    )

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Switch
                label={t('users:different_from_residence')}
                onChange={onChangeToggleSwitch}
                defaultChecked={isDifferentDomicile}
                size="sm"
            />
            {isDifferentDomicile && (
                <>
                    <Grid columns={6} columnGap={2} rowGap={4} fullWidth>
                        <GridItem startColumn={1} endColumn={4}>
                            <InputAddressAutocomplete
                                label={t('commons:address')}
                                placeholder={t('commons:select')}
                                onLatLngFound={latLng => setAddressCoordinates(latLng)}
                                errorMessage={form.formState.errors.domicile?.address?.message}
                                onChange={onSelectAddressAutocomplete}
                                showManualForm={() => setIsManualFormModalActive(true)}
                                predictionTypes={['address']}
                            />
                        </GridItem>
                    </Grid>

                    {domicileAddress && (
                        <>
                            <StyledDomicileAddressCardWrapper columns={6} fullWidth $isOpen={!!domicileAddress}>
                                <GridItem startColumn={1} endColumn={4}>
                                    <UserAddressCard
                                        icon={<MarkerPin01Icon size={20} />}
                                        kicker={t('commons:address')}
                                        title={`${domicileAddress}${domicileStreetNumber && `, ${domicileStreetNumber}`}`}
                                        subtitle={`${domicilePostalCode} ${domicileCity?.name} (${domicileProvince?.acronym})`}
                                        border="right"
                                    />
                                </GridItem>
                                <GridItem startColumn={4} endColumn={7}>
                                    <UserAddressCard
                                        icon={<MedicalCrossIcon size={20} />}
                                        kicker={t('commons:asl')}
                                        title={isOutOfRegion ? t('commons:out_region') : domicileAslDistrict?.asl?.name}
                                        subtitle={
                                            isOutOfRegion
                                                ? t('commons:asl_location_not_found')
                                                : domicileAslDistrict?.name
                                        }
                                        isLoading={aslDistrictQuery.isFetching}
                                        isError={isOutOfRegion}
                                    />
                                </GridItem>
                            </StyledDomicileAddressCardWrapper>
                            <StyledDomicileAddressHint gap={1}>
                                {t('commons:residence_hint_label')}
                                <Button
                                    size="xs"
                                    variant="tertiary"
                                    shape="text"
                                    onClick={() => setIsManualFormModalActive(true)}
                                >
                                    {t('commons:edit_fields')}
                                </Button>
                            </StyledDomicileAddressHint>
                        </>
                    )}

                    {isManualFormModalActive && (
                        <UserDomicileManualFormModal
                            onClose={() => setIsManualFormModalActive(false)}
                            defaultValues={{
                                isDifferentDomicile,
                                domicile: {
                                    address: domicileAddress,
                                    city: domicileCity,
                                    streetNumber: domicileStreetNumber,
                                    province: domicileProvince,
                                    postalCode: domicilePostalCode
                                },
                                domicileAslDistrict
                            }}
                        />
                    )}
                </>
            )}
        </Flexbox>
    )
}
